import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { WalletAddress } from '../../../../../components/wallet-address';
import Global from '../../../../../../store/global-selectors';
import { FORM_REQUIREMENTS } from '../../../../../utilities/constants';
import { FIELD_NAMES } from '../../../../../../address/address-fields/constants';
import { BUSINESS, PERSONAL } from '../../../../constants';
import { useWalletRegistrationErrorContext } from '../../../contexts/wallet-registration-error-context';
import { useWalletRegistrationFormContext } from '../../../contexts/wallet-registration-form-context';
import { AddressDropdown } from '../../../../../../pages/settings/payment-and-subscription/address-dropdown';
import { WalletAddressSchema } from '../../../../../components/wallet-address/wallet-address-schema';
import { PHONE_NEEDED_FOR_SOME_CARRIERS } from '../../../../../../address/sublabel-constants';
import { PickupAddressSchema } from '../pickup-address-schema';

export const WalletBillingAddress = ({ accountType, markFormComplete, isPickupSameAsBilling }) => {
  const [selectedAddress, setSelectedAddress] = useState();
  const shipFroms = useSelector(Global.selectSellerShipFroms);

  const { CITY, COUNTRY_CODE, LINE1, LINE2, LINE3, NAME, PHONE, POSTAL_CODE, STATE, COMPANY } = FIELD_NAMES;

  const { walletForm, updateWalletForm, submittingRegistration } = useWalletRegistrationFormContext();
  const { validationErrors } = useWalletRegistrationErrorContext();

  const onChange = useCallback(
    ({ propertyName, value }) => {
      // If the user selects the Personal radio option then we need to autofill the company field with the name value.
      // We do the same thing on the backend if the company field was left blank. This just helps pass validation
      if (accountType === PERSONAL && propertyName === NAME) {
        updateWalletForm([FORM_REQUIREMENTS.BILLING_ADDRESS, COMPANY], value);
      }
      if (isPickupSameAsBilling && accountType === PERSONAL && propertyName === NAME) {
        updateWalletForm([FORM_REQUIREMENTS.PICKUP_ADDRESS, COMPANY], value);
      }

      updateWalletForm([FORM_REQUIREMENTS.BILLING_ADDRESS, propertyName], value);
      if (isPickupSameAsBilling) {
        updateWalletForm([FORM_REQUIREMENTS.PICKUP_ADDRESS, propertyName], value);
      }
    },
    [COMPANY, NAME, accountType, isPickupSameAsBilling, updateWalletForm],
  );

  const onValidationChange = useCallback(
    (isValid) => {
      markFormComplete(FORM_REQUIREMENTS.BILLING_ADDRESS, isValid);
      if (isPickupSameAsBilling) {
        markFormComplete(FORM_REQUIREMENTS.PICKUP_ADDRESS, isValid);
      }
    },
    [markFormComplete, isPickupSameAsBilling],
  );

  const onAddressChange = useCallback(
    (addressSelection) => {
      const keyArray = [CITY, COUNTRY_CODE, LINE1, LINE2, LINE3, NAME, PHONE, POSTAL_CODE, STATE];
      Object.keys(addressSelection).forEach((key) => {
        if (keyArray.includes(key)) {
          updateWalletForm([FORM_REQUIREMENTS.BILLING_ADDRESS, key], addressSelection[key]);
        }
      });
      setSelectedAddress(addressSelection);
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [updateWalletForm],
  );

  const validationSchema = isPickupSameAsBilling ? PickupAddressSchema : WalletAddressSchema;
  const hiddenFields = accountType === PERSONAL ? [COMPANY] : [];

  const subLabels = {
    [PHONE]: PHONE_NEEDED_FOR_SOME_CARRIERS,
  };

  return (
    <>
      {!!shipFroms.length && <AddressDropdown onAddressChange={onAddressChange} selectedAddress={selectedAddress} shipFroms={shipFroms} />}
      <WalletAddress
        accountType={accountType}
        address={walletForm?.[FORM_REQUIREMENTS.BILLING_ADDRESS]}
        disabled={submittingRegistration}
        disableEmail
        errors={validationErrors?.[FORM_REQUIREMENTS.BILLING_ADDRESS]}
        hiddenFields={hiddenFields}
        onChange={onChange}
        onValidationChange={onValidationChange}
        subLabels={subLabels}
        type='v2'
        validationSchema={validationSchema}
      />
    </>
  );
};

WalletBillingAddress.propTypes = {
  accountType: PropTypes.oneOf([PERSONAL, BUSINESS]).isRequired,
  markFormComplete: PropTypes.func.isRequired,
  isPickupSameAsBilling: PropTypes.bool.isRequired,
};
